import { adminAccessArea } from './admin-access-target';
/**
 * featureFlagCanMatch — `CanMatchFn` factory that gates a route on a
 * PostHog feature flag.
 *
 * RESPONSIBILITY: when a route mounts behind a tenant feature flag, the
 * shell-side rail visibility check (`featureFlags.watch(key)`) is
 * normally enough — but direct URL hits (bookmark, paste, e-mail) bypass
 * the rail entirely. Pair `canMatch: [featureFlagCanMatch('my-flag')]`
 * on those routes so an unauthenticated/un-flagged visitor falls
 * through to the next matching definition (the `**` wildcard, by
 * convention).
 *
 * FLAG SEMANTICS:
 *   • `flagEnabled()` returns true when the flag is boolean-true OR a
 *     non-empty multivariate string. This matches what the rail and the
 *     in-page `[ngIf]` already do, so URL gating stays consistent with
 *     visible UI gating.
 *   • When PostHog hasn't evaluated flags yet (`!featureFlags.ready()`),
 *     we treat the flag as OFF. The user is briefly redirected to the
 *     fall-through; once flags resolve the next navigation succeeds.
 *     This is the same behaviour the shell uses for rail-link visibility
 *     and matches the GAP_AUDIT §9.6 contract (production default OFF).
 *
 * EXAMPLE:
 *   {
 *     path: 'flow/inbox',
 *     canMatch: [featureFlagCanMatch('flow-captures')],
 *     loadComponent: () => …,
 *   }
 */
import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { FeatureFlagsService } from '../services/feature-flags.service';
import {
  FeatureFlagsViewerService,
  type FeatureFlagKey,
} from '../services/feature-flags-viewer.service';
import { ViewerService } from '../services/viewer.service';
import { isAdminPreview } from './admin-preview';

export function featureFlagCanMatch(flagKey: string): CanMatchFn {
  return () => {
    const featureFlags = inject(FeatureFlagsService);
    return featureFlags.flagEnabled(flagKey);
  };
}

/** Canonical PostHog flag key for the Flow Captures surface. Used by
 *  flow-shell rail visibility AND the /flow/inbox + /flow/captures/:id
 *  CanMatch guards — keep these in lockstep. */
export const FLOW_CAPTURES_FLAG = 'flow-captures';

/** Canonical PostHog flag key for the internal sensor-calibration alpha. */
export const SENSOR_CALIBRATION_ALPHA_FLAG = 'map-sensor-calibration-alpha';

/**
 * featureFlagGuard — `CanActivateFn` factory that gates a route on a
 * customer/division catalogue feature flag (NOT a PostHog flag — for
 * that see `featureFlagCanMatch` above).
 *
 * RESPONSIBILITY: when a route mounts behind a catalogue feature flag
 * (e.g. `/settings/alerts` behind `alerts.building`), an unauthenticated
 * URL hit must NOT reveal the surface exists. To stay consistent with
 * the backend `FeatureFlagGuard` decorator that throws `NOT_FOUND` (404)
 * rather than `FORBIDDEN` (403) — so the absence of the flag is
 * indistinguishable from the route never having existed — this guard
 * redirects to the wildcard 404 route on failure.
 *
 * USAGE:
 *   {
 *     path: 'settings/alerts',
 *     canActivate: [authGuard, adminGuard, featureFlagGuard('alerts.building')],
 *     loadComponent: () => …,
 *   }
 *
 * SEMANTICS:
 *   • Waits for `ViewerService.ensureLoaded()` so the viewer-side flag
 *     set is hydrated before the decision. Without the await the flag
 *     reads OFF on first navigation after a hard reload and the user
 *     gets bounced into the 404 page even when the flag is actually on.
 *   • Emits `emp.viewer.feature_flag.gated` with `{ flag_key, allowed }`
 *     so route-level gating is observable in the same funnel as
 *     directive-level gating.
 *   • Redirects to `/not-found` on failure (404 surface). Routes that
 *     would otherwise be reachable but for the flag are made invisible
 *     to non-entitled customers — matches the server-side guard.
 *   • Honours the dev-only `?adminPreview=1` bypass, like every other
 *     guard on the admin route chain (`authGuard`, `adminGuard`,
 *     `policyManagementGuard`). Without it a flag-gated admin surface
 *     cannot be reviewed locally at all: the flag is customer state, so
 *     it reads OFF with no backend and the route bounces to the home
 *     page. Never active in production builds — `isAdminPreview`
 *     short-circuits on `environment.production`.
 */
export function featureFlagGuard(flagKey: FeatureFlagKey): CanActivateFn {
  return async (route) => {
    if (isAdminPreview(route)) return true;

    const viewerService = inject(ViewerService);
    const featureFlags = inject(FeatureFlagsViewerService);
    const router = inject(Router);

    await viewerService.ensureLoaded();
    const allowed = featureFlags.is(flagKey);
    featureFlags.recordGate(flagKey, allowed);

    if (allowed) return true;
    // Optional-chained for the same reason as `anyActionGuard`: the router
    // always supplies `data`, direct callers in specs do not, and a guard
    // must not throw while deciding how to refuse.
    const area = adminAccessArea(route?.data?.['accessStateArea']);
    if (area) return router.createUrlTree(['/platform/access-status'], { queryParams: {
      area, reason: featureFlags.loadError() || !featureFlags.ready() ? 'unavailable' : 'feature-disabled',
    } });
    // Behave the same as a hard 404 — never reveal the route existed.
    // The `**` wildcard at the bottom of `app.routes.ts` redirects to
    // `/home-preview` (the default home surface), so the user lands on
    // the home page exactly as they would for any other non-existent URL.
    return router.createUrlTree(['/home-preview']);
  };
}

/**
 * featureFlagAnyGuard — `CanActivateFn` factory that gates a route on a
 * SET of catalogue feature flags with ANY-OF semantics: the route
 * activates when AT LEAST ONE of the keys is active for the viewer.
 *
 * RESPONSIBILITY: some surfaces aggregate several capabilities behind a
 * single URL (e.g. `/schedule` hosts rooms, desks, parking and events
 * sub-views, each with its own catalogue key). The route should stay
 * reachable while ANY of those capabilities is on; the page itself
 * hides the gated sub-views. Mirrors the ANY-OF `capabilityKeys`
 * semantics on `WorkspaceItem` (core/constants/navigation.ts) so nav
 * visibility and deep-link gating agree.
 *
 * USAGE:
 *   {
 *     path: 'schedule',
 *     canActivate: [authGuard, featureFlagAnyGuard(['rooms.booking', 'desks.booking'])],
 *     loadComponent: () => …,
 *   }
 *
 * SEMANTICS:
 *   • Waits for `ViewerService.ensureLoaded()` so the viewer-side flag
 *     set is hydrated before the decision (same as `featureFlagGuard`).
 *   • Emits `emp.viewer.feature_flag.gated` per key with that key's own
 *     allowed state, so each capability's gating exposure is measured
 *     individually in the same funnel as the single-key guard.
 *   • Redirects to `/home-preview` (the default home surface) when NO key
 *     is active — identical 404-style fall-through to `featureFlagGuard`.
 *   • Honours the same dev-only `?adminPreview=1` bypass.
 */
export function featureFlagAnyGuard(flagKeys: FeatureFlagKey[]): CanActivateFn {
  return async (route) => {
    // Same dev-only bypass as the single-key guard above.
    if (isAdminPreview(route)) return true;

    const viewerService = inject(ViewerService);
    const featureFlags = inject(FeatureFlagsViewerService);
    const router = inject(Router);

    await viewerService.ensureLoaded();
    const allowed = flagKeys.some(k => featureFlags.is(k));
    for (const k of flagKeys) {
      featureFlags.recordGate(k, featureFlags.is(k));
    }

    if (allowed) return true;
    // Behave the same as a hard 404 — never reveal the route existed.
    return router.createUrlTree(['/home-preview']);
  };
}
